import {
  type GetAllExamEventsByExamCenterCodeQuery,
  RegistrationType,
  RegistrationStatus,
} from '@exam-center/graphql';
import { type useNavigate } from '@exam-center/router';
import { dayjs } from '@shared/utils';

import {
  type ExamEventDailyScheduleProps,
  type ExamEventCardVariants,
  type ExamEventScheduleEmptyStateVariant,
} from '../../components';

export type ExamEvent =
  GetAllExamEventsByExamCenterCodeQuery['ecGetAllExamEventsByExamCenterCode']['examEvents'][number];

export const EXAM_EVENT_STATUS_TO_EXAM_EVENT_EMPTY_STATE_VARIANT: Record<
  ExamEvent['status'],
  ExamEventScheduleEmptyStateVariant
> = {
  SCHEDULED: 'scheduled',
  READY_TO_EXAM: 'ready-to-exam',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
};

const EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_CARD_VARIANT: Record<
  ExamEvent['registrationStatus'],
  ExamEventCardVariants
> = {
  OPEN_FOR_REGISTRATION: 'open-for-registration',
  REGISTRATION_CLOSED: 'registration-closed',
  SESSION_FULL: 'session-full',
  CANCELED: 'canceled',
  TEMPORARILY_CLOSED: 'temporarily-closed',
  SCHEDULED: 'scheduled',
};

export function mapExamEventsToExamEventDailyScheduleProps(
  examEvents: ExamEvent[],
  navigate: ReturnType<typeof useNavigate>,
): ExamEventDailyScheduleProps['examEvents'] {
  return examEvents.map((examEvent) => ({
    id: examEvent.id,
    type: examEvent.registrationType === RegistrationType.Public ? 'public' : 'in-house',
    variant:
      EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_CARD_VARIANT[examEvent.registrationStatus],
    time: `${dayjs(examEvent.examStartedAt).format('HH:mm')} - ${dayjs(
      examEvent.examEndedAt,
    ).format('HH:mm')}`,
    title: examEvent.name,
    location: examEvent.examLocation?.nameTh ?? '',
    seat: examEvent.numberOfExaminees ?? 0,
    roundNumber: examEvent.roundNumber,
    maximumSeat: examEvent.seat,
    examCenterCode: examEvent.examCenterCode,
    onClick: () =>
      navigate(`/:examCenterCode/exam-events/:examEventId`, {
        params: {
          examCenterCode: examEvent.examCenterCode,
          examEventId: examEvent.id,
        },
      }),
  }));
}

const REGISTRATION_STATUS_ORDER = [
  RegistrationStatus.OpenForRegistration,
  RegistrationStatus.SessionFull,
  RegistrationStatus.TemporarilyClosed,
  RegistrationStatus.Canceled,
  RegistrationStatus.RegistrationClosed,
];

export function sortExamEvents(examEvents: ExamEvent[]) {
  return [...examEvents].sort((a, b) => {
    // 1. First sort by registrationStatus
    if (a.registrationStatus !== b.registrationStatus) {
      const indexA = REGISTRATION_STATUS_ORDER.indexOf(a.registrationStatus);
      const indexB = REGISTRATION_STATUS_ORDER.indexOf(b.registrationStatus);
      return indexA - indexB;
    }

    const startedAtA = dayjs(a.examStartedAt);
    const startedAtB = dayjs(b.examStartedAt);

    // 2. Then sort by examStartedAt
    if (!startedAtA.isSame(startedAtB)) {
      return startedAtA.isBefore(startedAtB) ? -1 : 1;
    }

    // 3. Finally sort by roundNumber
    return a.roundNumber - b.roundNumber;
  });
}
